// Admin User Management — TanStack Query mutation for
// POST /api/v1/admin/users/:id/unblock.
//
// No request body (the endpoint takes none). Fresh Idempotency-Key per
// click (crypto.randomUUID).

import { useMutation, useQueryClient } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type UnblockUserResponse = {
  data: {
    user_id: string;
    was_blocked: boolean;
  };
};

export type UnblockUserError = {
  code: string;
  message: string;
  status: number;
};

async function unblockUser(userId: string): Promise<UnblockUserResponse> {
  const response = await fetch(`/api/v1/admin/users/${userId}/unblock`, {
    method: "POST",
    credentials: "include",
    headers: {
      "X-Idempotency-Key": crypto.randomUUID(),
    },
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: UnblockUserError = {
      code: "ADMIN_UNBLOCK_FAILED",
      message: `Unblock failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          status: response.status,
        };
      }
    } catch {
      // body not JSON
    }
    throw detail;
  }
  return (await response.json()) as UnblockUserResponse;
}

export function useUnblockUser(userId: string) {
  const queryClient = useQueryClient();
  return useMutation<UnblockUserResponse, UnblockUserError, void>({
    mutationFn: () => unblockUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
      queryClient.invalidateQueries({
        queryKey: ["admin-user-detail", userId],
      });
    },
  });
}
